import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, partnerFileUrl, errorText, formatDateTime } from "@/lib/partnerApi";
import { P_CARD, P_H1, P_FIELD, P_LABEL, P_BTN, BackLink, UrgentCard, PageLoading } from "@/components/partner/PartnerUI";
import { PhotoPicker, photoIds, photosBusy } from "@/components/partner/PhotoPicker";

function InternalNote() {
    return (
        <p className="flex items-start gap-2 text-sm text-stone-600 bg-white/70 rounded-2xl p-3 border border-stone-200" data-testid="partner-thread-internal-note">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Conversa interna com O Homem da Obra: o cliente não tem acesso. Decisões sobre trabalhos, materiais, prazos ou alterações são sempre tomadas por O Homem da Obra.
        </p>
    );
}

export function PartnerNewThread() {
    const { leadId } = useParams();
    const nav = useNavigate();
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [urgent, setUrgent] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const uploading = photosBusy(photos);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await partnerApi.post(`/obras/${leadId}/threads`, { title, body, urgent, photo_file_ids: photoIds(photos) });
            toast.success("Questão enviada.");
            nav(`/parceiros/obras/${leadId}/questoes/${data.id}`, { replace: true });
        } catch (err) {
            toast.error(errorText(err));
            setBusy(false);
        }
    };

    return (
        <>
            <BackLink to={`/parceiros/obras/${leadId}`} label="Voltar à obra" />
            <h1 className={P_H1}>Problema ou questão</h1>
            <InternalNote />
            <form onSubmit={submit} className={`${P_CARD} space-y-5`} data-testid="partner-thread-form">
                <label className="block">
                    <span className={P_LABEL}>Assunto *</span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Ex.: Tubagem não prevista na cozinha" className={P_FIELD} data-testid="partner-thread-title" />
                </label>
                <label className="block">
                    <span className={P_LABEL}>Mensagem *</span>
                    <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} className={`${P_FIELD} resize-none`} data-testid="partner-thread-body" />
                </label>
                <div>
                    <span className={P_LABEL}>Fotografias</span>
                    <PhotoPicker leadId={leadId} photos={photos} setPhotos={setPhotos} testid="partner-thread-photos" />
                </div>
                <label className="flex items-start gap-3 min-h-[48px] cursor-pointer">
                    <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} className="mt-1 w-5 h-5 accent-red-700" data-testid="partner-thread-urgent" />
                    <span className="text-[15px] text-stone-800">É urgente (segurança, danos ou impedimento à execução)</span>
                </label>
                {urgent && <UrgentCard compact />}
                <button type="submit" disabled={busy || uploading || !title.trim() || !body.trim()} className={`${P_BTN} !w-full`} data-testid="partner-thread-submit">
                    {busy || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Enviar questão
                </button>
            </form>
        </>
    );
}

export function PartnerThread() {
    const { leadId, threadId } = useParams();
    const [t, setT] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await partnerApi.get(`/threads/${threadId}`);
            setT(data);
            if (data.unread) partnerApi.post(`/threads/${threadId}/read`).catch(() => {});
        } catch (e) {
            setError(errorText(e, "Questão não encontrada"));
        }
    }, [threadId]);
    useEffect(() => { load(); }, [load]);

    const back = <BackLink to={`/parceiros/obras/${leadId}`} label="Voltar à obra" />;
    if (error) return <>{back}<div className={P_CARD}>{error}</div></>;
    if (!t) return <PageLoading />;
    return (
        <>
            {back}
            <div>
                <h1 className={`${P_H1} break-words`} data-testid="partner-thread-heading">{t.title}</h1>
                <div className="mt-3 flex flex-wrap gap-2">
                    {t.urgent && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">Urgente</span>}
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${t.status === "open" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`} data-testid="partner-thread-status">
                        {t.status === "open" ? "Aberta" : "Resolvida"}
                    </span>
                </div>
            </div>
            <InternalNote />
            <ol className="space-y-3" data-testid="partner-thread-messages">
                {t.messages.map((m) => <Bubble key={m.id} m={m} />)}
            </ol>
            <ReplyForm leadId={leadId} t={t} onSent={setT} />
        </>
    );
}

function Bubble({ m }) {
    const mine = m.author === "partner";
    return (
        <li className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`partner-message-${m.id}`}>
            <div className={`max-w-[88%] rounded-2xl px-4 py-3 ${mine ? "bg-[hsl(var(--forest))] text-white rounded-br-md" : "bg-white border border-stone-200 text-stone-900 rounded-bl-md"}`}>
                <div className={`text-xs font-semibold mb-1 ${mine ? "text-white/75" : "text-[hsl(var(--copper))]"}`}>{m.author_name || (mine ? "Eu" : "O Homem da Obra")}</div>
                <p className="text-base leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>
                {mine && (m.photo_file_ids || []).length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-1.5">
                        {m.photo_file_ids.map((id) => <img key={id} src={partnerFileUrl(id)} alt="" className="aspect-square w-full object-cover rounded-lg" loading="lazy" />)}
                    </div>
                )}
                <div className={`text-xs mt-1.5 ${mine ? "text-white/60" : "text-stone-500"}`}>{formatDateTime(m.created_at)}</div>
            </div>
        </li>
    );
}

function ReplyForm({ leadId, t, onSent }) {
    const [body, setBody] = useState("");
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const uploading = photosBusy(photos);

    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await partnerApi.post(`/threads/${t.id}/messages`, { body, photo_file_ids: photoIds(photos) });
            setBody("");
            setPhotos([]);
            onSent(data);
            toast.success("Mensagem enviada.");
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={send} className={`${P_CARD} space-y-4`} data-testid="partner-thread-reply-form">
            {t.status === "resolved" && <p className="text-sm text-emerald-900 bg-emerald-50 rounded-2xl p-3">Esta questão está resolvida. Se responder, volta a ficar aberta.</p>}
            <label className="block">
                <span className={P_LABEL}>Responder</span>
                <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} className={`${P_FIELD} resize-none`} data-testid="partner-thread-reply" />
            </label>
            <PhotoPicker leadId={leadId} photos={photos} setPhotos={setPhotos} testid="partner-reply-photos" />
            <button type="submit" disabled={busy || uploading || !body.trim()} className={`${P_BTN} !w-full`} data-testid="partner-thread-send">
                {busy || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Enviar mensagem
            </button>
        </form>
    );
}
